import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getApiErrorMessage } from "../services/apiErrors";
import {
  createConnector,
  fetchConnectorTypes,
  fetchConnectors,
  uploadFileToConnector,
} from "../services/connectorsV2/connectorsV2Api";
import {
  CSV_CONTENT_TYPE,
  fileStem,
  findCsvTypeId,
  isCsvFile,
  uniqueConnectorName,
} from "../services/fileStory/fileStoryFlow";
import { FILE_STORY_COPY } from "../services/fileStory/fileStory.copy";

export const FILE_STORY_PROMPT_KEY = "csvStoryInitialPrompt";

/** A file on the new architecture: connector → upload → the progress page, which does the rest. */
export const useFileStoryFlowV2 = (workspaceId: string) => {
  const navigate = useNavigate();

  /** Resolves to an error message, or null once the progress page is open. */
  return useCallback(
    async (file: File, prompt: string): Promise<string | null> => {
      if (!isCsvFile(file)) return FILE_STORY_COPY.notCsv;
      if (file.size === 0) return FILE_STORY_COPY.empty;
      localStorage.setItem(FILE_STORY_PROMPT_KEY, prompt);
      try {
        const catalogId = findCsvTypeId(await fetchConnectorTypes());
        if (!catalogId) return FILE_STORY_COPY.noCsvType;
        const existing = await fetchConnectors({ limit: 100, offset: 0 }).then(
          (page) => page.data.map((connector) => connector.connection_name),
          () => [] as string[],
        );
        const connector = await createConnector({
          catalog_id: catalogId,
          connection_name: uniqueConnectorName(fileStem(file.name), existing),
        });
        const upload = await uploadFileToConnector(connector.id, file, undefined, CSV_CONTENT_TYPE);
        const query = new URLSearchParams({ connectorId: connector.id, uploadId: upload.id, file: file.name });
        navigate(`/workspace/${workspaceId}/csv-loading?${query.toString()}`);
        return null;
      } catch (caught) {
        return getApiErrorMessage(caught, FILE_STORY_COPY.uploadFailed);
      }
    },
    [workspaceId, navigate],
  );
};
